/** "Add sandbox" on a production operator (S5a; SBX D8, O4): creates `<code>sbx` through the
 *  provisioning service — a tenant of its own, paired with this one.
 *
 *  Needs `console_sandbox_manage` and `console_top_manage` (it creates a tenant). The admin is a separate
 *  account (O3), created only when an e-mail is given. "Use production's vendor key" (O16) is offered only
 *  while the production-data gate is open (`SANDBOX_PRODUCTION_DATA`, owner action O-10) and shows its
 *  warning. The request is sent straight from the form, never through `useMutation`: it may carry the
 *  admin's password (lesson "A credential must not ride useMutation"). */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  sandboxApi,
  sandboxErrorMessage,
  sandboxKeys,
  type OutboundMode,
  type SandboxAllowlist,
} from '@/api/sandbox';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { useSnackbar } from '@/components/Snackbar';

import {
  AllowlistEditor,
  allowlistErrorsOf,
  EMPTY_ALLOWLIST,
  type ListKey,
} from './AllowlistEditor';
import { OutboundModeRadios } from './OutboundSection';

const AddSchema = z
  .object({
    admin_email: z.union([z.literal(''), z.string().trim().email('Not an e-mail address')]),
    admin_password: z.string(),
    admin_first_name: z.string().trim().max(150),
    admin_last_name: z.string().trim().max(150),
    outbound_mode: z.enum(['outbox', 'allowlist', 'allow_all']),
    allowlist: z.object({
      domains: z.array(z.string()),
      emails: z.array(z.string()),
      phones: z.array(z.string()),
    }),
    use_production_vendor_key: z.boolean(),
    copy_itineraries: z.boolean(),
    clone_content: z.boolean(),
    comment: z.string().max(250),
  })
  .refine((v) => !v.admin_email || v.admin_password.length >= 8, {
    path: ['admin_password'],
    message: 'The sandbox admin is a separate account: give it a password (8 characters or more).',
  });
type AddValues = z.infer<typeof AddSchema>;

const DEFAULTS: AddValues = {
  admin_email: '',
  admin_password: '',
  admin_first_name: '',
  admin_last_name: '',
  outbound_mode: 'outbox',
  allowlist: { ...EMPTY_ALLOWLIST },
  use_production_vendor_key: false,
  copy_itineraries: false,
  clone_content: true,
  comment: '',
};

export function AddSandboxSection({
  to,
  canCreate,
}: {
  to: TourOperatorDetail;
  canCreate: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listErrors, setListErrors] = useState<Partial<Record<ListKey, string>>>({});
  const options = useQuery({
    queryKey: sandboxKeys.options,
    queryFn: sandboxApi.options,
    enabled: canCreate,
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AddValues>({ defaultValues: DEFAULTS, resolver: zodResolver(AddSchema) });
  const code = `${to.code}sbx`;
  const cloneContent = watch('clone_content');
  const useProductionKey =
    watch('use_production_vendor_key') && Boolean(options.data?.production_data_enabled);

  if (!canCreate) {
    return (
      <Typography variant="body2" color="text.secondary">
        {to.name} has no sandbox. Creating one needs the sandbox and tour-operator manage
        permissions.
      </Typography>
    );
  }

  const onSubmit = async (v: AddValues) => {
    setSubmitting(true);
    setError(null);
    setListErrors({});
    try {
      const created = await sandboxApi.create(to.id, {
        ...(v.admin_email
          ? {
              admin_email: v.admin_email,
              admin_password: v.admin_password,
              admin_first_name: v.admin_first_name,
              admin_last_name: v.admin_last_name,
            }
          : {}),
        outbound_mode: v.outbound_mode,
        allowlist: v.allowlist as SandboxAllowlist,
        ...(options.data?.production_data_enabled
          ? { use_production_vendor_key: v.use_production_vendor_key }
          : {}),
        copy_itineraries: v.clone_content && v.copy_itineraries,
        clone_content: v.clone_content,
        ...(v.comment.trim() ? { comment: v.comment.trim() } : {}),
      });
      // The password must not outlive the request in the form either.
      setValue('admin_password', '');
      snackbar.show(
        `Sandbox ${created.code} created${created.clone_queued ? ' — production’s content is being copied' : ''}.`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['tourOperators'] });
      navigate(`/tour-operators/${created.id}/sandbox`);
    } catch (e) {
      setListErrors(allowlistErrorsOf(e));
      setError(sandboxErrorMessage(e, 'The sandbox could not be created.'));
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'grid', gap: 2.5, maxWidth: 880 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        Creates <strong>{code}</strong>, a tenant of its own paired with {to.code}: production’s
        behaviour settings are copied — never its credentials or the client’s own endpoints — with
        the sandbox onboarding profile, a 14-day request log and a default import key (
        <code>tosbx_</code>). Chat stays off until the sandbox has its own Twilio account.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
          Sandbox admin (optional)
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            size="small"
            label="Admin e-mail"
            {...register('admin_email')}
            error={Boolean(errors.admin_email)}
            helperText={
              errors.admin_email?.message ??
              'A separate account from production’s; none without an e-mail.'
            }
          />
          <TextField
            size="small"
            label="Admin password"
            type="password"
            autoComplete="new-password"
            {...register('admin_password')}
            error={Boolean(errors.admin_password)}
            helperText={errors.admin_password?.message}
          />
          <TextField size="small" label="First name" {...register('admin_first_name')} />
          <TextField size="small" label="Last name" {...register('admin_last_name')} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1.3fr)' },
        }}
      >
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            Outbound mode
          </Typography>
          <Controller
            name="outbound_mode"
            control={control}
            render={({ field }) => (
              <OutboundModeRadios
                value={field.value as OutboundMode}
                onChange={field.onChange}
                allowAllRefusal={
                  useProductionKey ? `${code} would use production’s vendor key` : null
                }
              />
            )}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            Allowlist
          </Typography>
          <Controller
            name="allowlist"
            control={control}
            render={({ field }) => (
              <AllowlistEditor
                value={field.value}
                onChange={field.onChange}
                ownDomains={options.data?.own_domains ?? []}
                errors={listErrors}
              />
            )}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
          Copied from {to.code}
        </Typography>
        <Stack>
          <Controller
            name="clone_content"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Notifications, templates, strings, tag categories and launch policies (after creation)"
              />
            )}
          />
          <Controller
            name="copy_itineraries"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                disabled={!cloneContent}
                control={
                  <Checkbox
                    size="small"
                    checked={cloneContent && field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Catalog itineraries too (their images are copied, never shared with production)"
              />
            )}
          />
          {options.data?.production_data_enabled && (
            <Controller
              name="use_production_vendor_key"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Use production’s vendor key"
                  />
                  {field.value && (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      {code} will import {to.code}’s real bookings. Its sends go to the outbox and
                      the allowlist only; allow-all is off while the key matches. Both operators
                      show a production-key banner.
                    </Alert>
                  )}
                </Box>
              )}
            />
          )}
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label="Comment"
          placeholder="Why — kept in the audit record"
          inputProps={{ maxLength: 250 }}
          sx={{ flex: 1, maxWidth: 480 }}
          {...register('comment')}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Creating…' : `Create ${code}`}
        </Button>
      </Stack>
    </Box>
  );
}
